import React, { useState } from 'react';
import { Box, Typography, styled, useTheme } from '@mui/material';
import { BarChart, XAxis, YAxis, Bar, ResponsiveContainer, Tooltip } from 'recharts';

import CustomTooltip from './Tooltip/CustomTooltip';
import { SocialIconsType } from '../../../../dto/Interfaces/IIconProps';
import { colors } from '../../../../utils/palette';
import SocialImages from '../../../../components/InfoCard/Details/SocialImages/SocialImages';

export type ConnectedData =
  | {
      name: SocialIconsType;
      connected: number;
      notconnected: number;
      unknown: number;
      total: number;
    }
  | undefined;

export interface ConnectedDataChartProps {
  data: ConnectedData[];
  type: 'connectedData';
}

const WrapperBox = styled(Box)<{ height?: string }>(({ theme, height }) => ({
  height: height || '80px',
  marginBottom: height ? theme.spacing(4) : 0,
  width: '100%',
  position: 'relative',
  '&:first-of-type': {
    marginTop: theme.spacing(2),
  },
}));

const TickBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  position: 'absolute',
  zIndex: 999,
  top: '-23px',
}));

const TickName = styled(Typography)(({ theme }) => ({
  marginLeft: theme.spacing(1),
  textTransform: 'capitalize',
}));

const ConnectedDataChart: React.FC<ConnectedDataChartProps> = ({ data }) => {
  const [tooltip, setTooltip] = useState<string>('');
  const theme = useTheme();

  const verticalBarData = data as ConnectedData[];

  const socialWrapper = verticalBarData
    .filter((el) => el && 'name' in el)
    .map((el, i) => {
      const name = el?.name;
      const dataRounded = {
        name,
        connected: Math.round(Number(el?.connected)),
        notconnected: Math.round(Number(el?.notconnected)),
        unknown: Math.round(Number(el?.unknown)),
      };
      return (
        <WrapperBox key={`${name}-${i}`} data-testid="VerticalBarChart">
          <TickBox>
            <SocialImages socials={[name || 'airbnb']} type="socialImages" />
            <TickName variant="subtitle2">{name}</TickName>
          </TickBox>
          <ResponsiveContainer height="100%" width="100%">
            <BarChart data={[dataRounded]} layout="vertical" barSize={10}>
              <XAxis type="number" axisLine={false} tickLine={false} tick={false} domain={[0, 100]} />
              <YAxis type="category" axisLine={false} tickLine={false} tick={false} hide={true} dataKey="name" />
              <Bar
                dataKey="connected"
                stackId="a"
                fill={colors.success.main}
                onMouseOver={() => setTooltip('connected')}
                radius={[
                  // will always be rounded on the left: if connected are present it is the first value
                  10,
                  // will be rounded on the right if not connected and unknown are not present
                  dataRounded.notconnected + dataRounded.unknown > 0 ? 0 : 10,
                  // will be rounded on the right if not connected and unknown are not present
                  dataRounded.notconnected + dataRounded.unknown > 0 ? 0 : 10,
                  // will always be rounded on the left: if connected are present it is the first value
                  10,
                ]}
              />
              <Bar
                dataKey="notconnected"
                stackId="a"
                fill={colors.error.main}
                onMouseOver={() => setTooltip('notconnected')}
                radius={[
                  // will be rounded on the left if connected are not present
                  dataRounded.connected > 0 ? 0 : 10,
                  // will be rounded on the right if unknown are not present
                  dataRounded.unknown > 0 ? 0 : 10,
                  // will be rounded on the right if unknown are not present
                  dataRounded.unknown > 0 ? 0 : 10,
                  // will be rounded on the left if connected are not present
                  dataRounded.connected > 0 ? 0 : 10,
                ]}
              />
              <Bar
                dataKey="unknown"
                stackId="a"
                fill={colors.almostWhite}
                onMouseOver={() => setTooltip('unknown')}
                radius={[
                  // will be rounded on the left if connected and not connected are not present
                  dataRounded.connected + dataRounded.notconnected > 0 ? 0 : 10,
                  // will always be rounded on the right: if unknown are present it is the last value
                  10,
                  // will always be rounded on the right: if unknown are present it is the last value
                  10,
                  // will be rounded on the left if connected and not connected are not present
                  dataRounded.connected + dataRounded.notconnected > 0 ? 0 : 10,
                ]}
              />
              <Tooltip
                cursor={{ fill: 'transparent' }}
                wrapperStyle={{
                  backgroundColor: colors.textSecondary,
                  width: 150,
                  borderRadius: 4,
                  textAlign: 'center',
                  color: colors.white,
                  marginTop: theme.spacing(-3),
                }}
                content={<CustomTooltip tooltip={tooltip} />}
              />
            </BarChart>
          </ResponsiveContainer>
        </WrapperBox>
      );
    });

  return (
    <Box component="div" marginTop={theme.spacing(5)}>
      {socialWrapper}
    </Box>
  );
};

export default ConnectedDataChart;
